import React from 'react';
import {
    Box, ExpandableSection, StatusIndicator, StatusIndicatorProps, Table,
} from '@amzn/awsui-components-react';
import { IdentifierResolution, ResolvedIdentifier } from './bulkIdentifierClientParser';
import { BulkPromotionContext } from 'src/services/bulk-promotion/BulkPromotionContext';

/**
 * Above this many rows the table starts collapsed.
 *
 * A large paste renders a row per component above the verdict and the result banner, burying the outcome on
 * the batch where it matters most. Below the threshold the table is itself the useful content.
 */
const COLLAPSE_ABOVE_ROWS = 25;

const RESOLUTION_BANDS: Record<string, [string, StatusIndicatorProps.Type]> = {
    // 'Resolved' overclaimed: the browser only reads and normalises a token, it cannot know the
    // component exists. Existence is preflight's job server-side. 'Accepted' states what happened.
    [IdentifierResolution.ACCEPTED]: ['Accepted', 'success'],
    [IdentifierResolution.DUPLICATE]: ['Duplicate', 'info'],
    [IdentifierResolution.RAW_UUID]: ['Not accepted', 'warning'],
    [IdentifierResolution.DECODE_FAILED]: ['Not accepted', 'warning'],
    [IdentifierResolution.DECODED_NOT_AN_ID]: ['Not accepted', 'warning'],
    // Named rather than a generic refusal: "no such name" and "several things have this name" need
    // different actions from the operator, and the Detail column carries the candidate ids for the second.
    [IdentifierResolution.NAME_NOT_FOUND]: ['Name not found', 'warning'],
    [IdentifierResolution.NAME_AMBIGUOUS]: ['Name is ambiguous', 'warning'],
};

/**
 * One row per pasted row, showing what each resolved to. Without it a bad decode and a bad
 * identifier look identical. Holds no parsing logic of its own.
 */
export default function IdentifierSummary() {
    const { bulkPromotionState } = React.useContext(BulkPromotionContext);
    const rows = bulkPromotionState.resolvedRows;
    if (rows.length === 0) {
        return null;
    }

    /*
     * The counts go in the header so a collapsed table still answers the question asked of it — was everything
     * accepted. Anything not accepted is worth opening the table for; a list of accepted rows is not.
     */
    const notAccepted = rows.filter(row =>
        row.resolution !== IdentifierResolution.ACCEPTED
        && row.resolution !== IdentifierResolution.DUPLICATE).length;
    const duplicates = rows.filter(row => row.resolution === IdentifierResolution.DUPLICATE).length;
    const summary = [
        `${rows.length - notAccepted - duplicates} accepted`,
        duplicates > 0 ? `${duplicates} duplicate` : null,
        notAccepted > 0 ? `${notAccepted} not accepted` : null,
    ].filter(Boolean).join(' · ');

    const table = (
        <Table
            variant="embedded"
            trackBy="rowKey"
            items={rows.map((row, index) => ({ ...row, rowKey: `${index}` }))}
            columnDefinitions={[
                {
                    id: 'input',
                    header: 'Pasted',
                    cell: (item: ResolvedIdentifier) => item.input,
                    isRowHeader: true,
                },
                {
                    id: 'resolvedId',
                    header: 'Resolved to',
                    cell: (item: ResolvedIdentifier) => {
                        if (!item.resolvedId) {
                            return '—';
                        }
                        return item.decoded ? `${item.resolvedId} (decoded)` : item.resolvedId;
                    },
                },
                {
                    id: 'resolution',
                    header: 'Outcome',
                    cell: (item: ResolvedIdentifier) => {
                        const [label, band] = RESOLUTION_BANDS[item.resolution] || ['Not accepted', 'warning'];
                        return <StatusIndicator type={band}>{label}</StatusIndicator>;
                    },
                },
                {
                    id: 'message',
                    header: 'Detail',
                    cell: (item: ResolvedIdentifier) => item.message || '',
                },
            ]}
            header={rows.length > COLLAPSE_ABOVE_ROWS
                ? undefined
                : <Box variant="h4">Identifiers ({rows.length})</Box>}
            empty={<Box textAlign="center">Nothing pasted yet.</Box>}
        />
    );

    if (rows.length <= COLLAPSE_ABOVE_ROWS) {
        return table;
    }

    // defaultExpanded is deliberately absent, so a large paste starts closed and the verdict and the result
    // banner sit within a screen of the button that produced them.
    return (
        <ExpandableSection
            variant="container"
            headerText={`Identifiers (${rows.length})`}
            headerDescription={summary}
        >
            {table}
        </ExpandableSection>
    );
}
